/**
 * EL CIERRE — lógica pura de la pantalla con la que se presenta el precio (E5, 2026-08-22).
 *
 * Tres dimensiones sobre los mismos datos (Teoría §6b, plan §5): RUBRO × ETAPA × VÍA.
 *
 *  · RUBRO: cada partida sabe de qué rubro es (`item.rubro`, o el tipo de diseño de su
 *    levantamiento, o la categoría del producto). «Solo esto» NO es una suma de renglones:
 *    corre `porCotizacion` COMPLETO sobre la cotización filtrada a ese rubro, porque los
 *    días de obra salen de los minutos de toda la cotización, el traslado de los días y
 *    el UAFIR ajusta el precio (PRODUCT.md, Positioning). Un subtotal dentro del total no
 *    es el precio de ese rubro solo, y «Desprender» daría otra cifra frente al cliente.
 *
 *  · ETAPA: la `recomendada` por defecto es la columna que cierra los HALLAZGOS DE
 *    SEGURIDAD (Esencial cuando los hay). El cliente tipo llega tras «olor a quemado»;
 *    recomendarle la más cara es la venta por empujón que La Visita §«NO hagamos» prohíbe.
 *    Sin hallazgos, la del medio si tiene contenido real; si no, la más completa que exista.
 *    Evidencia: Simonson 1989 (efecto de compromiso), Scheibehenne 2010 (tres opciones no
 *    paralizan si hay recomendación). El vendedor puede cambiarla; queda en `cot.recomendada`.
 *
 *  · VÍA: lo modular se promete EN FIRME; lo «por definir» (madera del taller) es vía de
 *    proyecto: estimado con rango y anticipo de diseño. No se suman en el mismo total.
 *
 * Líneas de confianza CONDICIONADAS a que sean ciertas (LFPC art. 32: publicidad
 * comprobable; art. 40: «garantía» solo con alcance y mecanismo). «Dictamen firmado» solo
 * cuando existe un dictamen; «su Revisión se acredita» solo con Revisión registrada y obra
 * que alcance el umbral (PRODUCT.md: 600 MXN acreditables contra obra ≥ 9,000).
 */

import { porCotizacion } from './calculo.js'
import { esOpciones, rotulo } from '../bloques.js'

export const RUBRO_POR_CATEGORIA = {
  cto: 'electrico', tab: 'electrico', seg: 'electrico', med: 'electrico', dia: 'electrico', ilu: 'electrico',
  aut: 'smart',
  arq: 'taller', ext: 'taller', fach: 'taller', int: 'taller', mue: 'taller', art: 'taller', luz: 'taller', env: 'taller', nic: 'taller'
}
export const NOMBRE_RUBRO = {
  electrico: 'Eléctrico', smart: 'Smart home', taller: 'Madera y acero', riego: 'Riego',
  camaras: 'Videovigilancia', wifi: 'Wi-Fi', iluminacion: 'Iluminación', otro: 'Otros'
}

/** Umbral a partir del cual la Revisión de 600 MXN se acredita (PRODUCT.md, Operating Context). */
export const UMBRAL_ACREDITACION_MXN = 9000
export const REVISION_MXN = 600

export function rubroDeItem(item, producto) {
  if (item?.rubro) return item.rubro
  const tipo = item?.def?.disenio?.tipo || producto?.disenio?.tipo
  if (tipo) return tipo === 'alimentacion' ? 'electrico' : tipo
  return RUBRO_POR_CATEGORIA[producto?.cat] || 'otro'
}

/** Rubros presentes en la cotización, con cuántas partidas tiene cada uno. */
export function rubrosDe(cot, P) {
  const cuenta = {}
  for (const h of cot?.habitaciones || [])
    for (const it of h.items || []) {
      const r = rubroDeItem(it, P?.get?.(it.sku))
      cuenta[r] = (cuenta[r] || 0) + 1
    }
  return Object.entries(cuenta).map(([rubro, partidas]) => ({ rubro, nombre: NOMBRE_RUBRO[rubro] || rubro, partidas }))
}

/** La misma cotización con SOLO las partidas de un rubro. No muta. */
export function filtrarPorRubro(cot, rubro, P) {
  return {
    ...cot,
    habitaciones: (cot.habitaciones || []).map(h => ({
      ...h, items: (h.items || []).filter(it => rubroDeItem(it, P?.get?.(it.sku)) === rubro)
    }))
  }
}

/** «Solo esto»: el precio de un rubro como si fuera la única obra. Cálculo completo. */
export const soloEsto = (cot, rubro, ctx) => porCotizacion(filtrarPorRubro(cot, rubro, ctx.P), ctx)

/** Columnas con contenido (total > 0), en orden. Dos columnas idénticas no se pintan. */
export function columnasActivas(cot, val) {
  const activas = [1, 2, 3].filter(e => (val[e]?.total || 0) > 0)
  if (esOpciones(cot)) return activas
  // En etapas el precio mostrado es el ACUMULADO; si una etapa no agrega nada, se omite.
  const salida = []
  let acumulado = 0
  for (const e of activas) {
    acumulado += val[e].total
    if (!salida.length || acumulado !== salida[salida.length - 1].acumulado) salida.push({ e, acumulado })
  }
  return salida.map(s => s.e)
}

/** El importe que el cliente paga si elige la columna `e`. */
export const importeDeColumna = (cot, val, e) =>
  esOpciones(cot) ? val[e].total : [1, 2, 3].filter(x => x <= e).reduce((a, x) => a + (val[x]?.total || 0), 0)

export function recomendadaPorDefecto(cot, val, objetos) {
  const activas = columnasActivas(cot, val)
  if (!activas.length) return null
  if (cot?.recomendada && activas.includes(cot.recomendada)) return cot.recomendada
  const haySeguridad = Object.values(objetos || {}).some(o => o.tipo === 'hallazgo')
  if (haySeguridad) return activas[0]
  if (activas.includes(2)) return 2
  return activas[activas.length - 1]
}

/** Vía: qué hay en firme y qué va por proyecto. */
export function viaDe(cot, P) {
  let firme = 0, proyecto = []
  for (const h of cot?.habitaciones || [])
    for (const it of h.items || []) {
      const p = P?.get?.(it.sku)
      if (p?.porDefinir) proyecto.push({ nombre: p.nombre || it.sku, recinto: h.nombre })
      else firme++
    }
  return { firme, proyecto }
}

/**
 * Líneas de confianza. Cada una sale SOLO si es cierta.
 * @param hayRevision  existe en la bitácora de la casa una intervención con el protocolo de la Revisión
 * @param hayDictamen  esa intervención tiene pasos sellados (hay dictamen que firmar)
 */
export function lineasDeConfianza({ hayRevision, hayDictamen, importeRecomendada, marca }) {
  const lineas = []
  const credenciales = ['Ingeniero titulado', 'Cédula profesional']
  if (hayDictamen) credenciales.push('Dictamen firmado')
  lineas.push(credenciales.join(' · '))
  if (hayRevision && importeRecomendada >= UMBRAL_ACREDITACION_MXN)
    lineas.push(`Su Revisión de ${REVISION_MXN} MXN se acredita contra esta obra.`)
  else if (hayRevision)
    lineas.push(`La Revisión de ${REVISION_MXN} MXN se acredita a partir de ${UMBRAL_ACREDITACION_MXN.toLocaleString('es-MX')} MXN de obra.`)
  if (marca?.revocacion) lineas.push(marca.revocacion)
  return lineas
}

const mx = n => '$' + Math.round(n || 0).toLocaleString('es-MX') + ' MXN'

/** El mensaje de WhatsApp: cabe en uno, dice lo que importa, nada de presión. */
export function resumenWhatsApp({ marca, cliente, cot, val, recomendada, lineas }) {
  const partes = [
    `${marca.nombre} · ${marca.descriptor}`,
    `Propuesta para ${cliente?.nombre || 'usted'} — ${cot.nombre} (${cot.fecha})`,
    ''
  ]
  for (const e of columnasActivas(cot, val)) {
    const marca_ = e === recomendada ? ' ← recomendada' : ''
    partes.push(`${rotulo(cot, e)}: ${mx(importeDeColumna(cot, val, e))}${marca_}`)
  }
  if (esOpciones(cot)) partes.push('(Son alternativas: se contrata una.)')
  partes.push('')
  for (const l of lineas) partes.push(l)
  partes.push('')
  partes.push(marca.fraseMadre)
  if (marca.contacto?.sitio) partes.push(marca.contacto.sitio)
  return partes.join('\n')
}

export const ligaWhatsApp = (telefono, texto) => {
  const num = (telefono || '').replace(/\D/g, '')
  const destino = num ? (num.length === 10 ? '52' + num : num) : ''
  return `https://wa.me/${destino}?text=${encodeURIComponent(texto)}`
}
